import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_KEY = "ordo_rex_access_v1";

const PasswordGate = ({ children }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [checked, setChecked] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === "granted") {
      setUnlocked(true);
    }
    setChecked(true);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const expected = process.env.REACT_APP_SITE_PASSWORD;
    if (value.trim() === expected) {
      localStorage.setItem(STORAGE_KEY, "granted");
      setUnlocked(true);
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  if (!checked) return null;

  return (
    <>
      {unlocked && children}
      <AnimatePresence>
        {!unlocked && (
          <motion.div
            key="gate"
            data-testid="password-gate"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[9999] bg-[#F4EFE7] flex items-center justify-center px-6"
          >
            <div className="grain absolute inset-0 opacity-[0.06] pointer-events-none" />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="relative w-full max-w-2xl text-center"
              style={{ x: shake ? [0, -8, 8, -6, 6, -3, 3, 0] : 0 }}
            >
              <img
                src="/logo-olive.png"
                alt="Ordo Rex Studio"
                className="mx-auto h-14 w-auto mb-10 opacity-90"
              />
              <p className="text-[11px] uppercase tracking-[0.36em] text-[#4c535a]/70 mb-6">Ordo Rex Studio</p>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em] text-[#4c535a] mb-4 whitespace-nowrap">
                Private wardrobe strategy
              </h1>
              <p className="font-serif italic font-light text-xl md:text-2xl leading-[1.2] text-[#4c535a]/85 mb-12">
                Currently under edit.
              </p>

              <motion.form
                onSubmit={handleSubmit}
                animate={shake ? { x: [0, -8, 8, -6, 6, -3, 3, 0] } : { x: 0 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-stretch gap-6 max-w-md mx-auto"
              >
                <input
                  type="password"
                  data-testid="password-input"
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    if (error) setError(false);
                  }}
                  autoFocus
                  placeholder="Access phrase"
                  className={`w-full bg-transparent border-b py-3 text-center font-serif text-lg md:text-xl text-[#4c535a] placeholder:text-[#4c535a]/35 outline-none transition-colors duration-300 ${
                    error ? "border-[#4c535a]" : "border-[#4c535a]/30 focus:border-[#4c535a]"
                  }`}
                />
                {error && (
                  <p data-testid="password-error" className="text-[11px] uppercase tracking-[0.28em] text-[#4c535a]">
                    Not quite. Try again.
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="password-submit"
                  className="mt-4 inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#4c535a] text-[#EFECE6] text-[11px] uppercase tracking-[0.28em] hover:bg-[#3d434a] transition-colors duration-500"
                >
                  Enter the studio
                </button>
              </motion.form>

              <p className="mt-12 text-[10px] uppercase tracking-[0.32em] text-[#4c535a]/40">
                Vienna — February 2027
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PasswordGate;
